import type { HistoryState, RouteLocationRaw, Router } from "vue-router";

/*
  UX roadmap #14: an overlay with an address (the sector flyout's /sectors/:sector, the content slide-over's ?preview=)
  pushes a history entry when it opens, so Back closes it. Closing it by hand must then leave that entry the same way,
  or Forward reopens it and Back steps through it again. The entry it pushed is marked in the history state, which the
  browser keeps across a reload; an overlay reached any other way (a shared link, a new tab, a refreshed cold link) has
  no entry of its own to go back from, so it replaces its address instead, and Back never leaves the app.
*/

export const OVERLAY_HISTORY_STATE_KEY = 'tmiOverlay' as const;

/** The `state` to push an overlay's entry with: `router.push( { ...location, state: overlayHistoryState() } )`. */
export function overlayHistoryState(): HistoryState {
    return { [ OVERLAY_HISTORY_STATE_KEY ]: true };
}

/** True when the current history entry is one an overlay pushed on opening. */
export function isOverlayHistoryEntry( router: Router ): boolean {
    return true === router.options.history.state?.[ OVERLAY_HISTORY_STATE_KEY ];
}

/**
 * Takes the address back to `fallback` for an overlay the member closed: Back when the overlay pushed the current
 * entry, otherwise a replace, so no entry is added. Resolves once a replace has landed; Back lands later, through the
 * router's own popstate handling. Only for a close the member made: Back is asynchronous in a browser, so an overlay
 * closed on the way to another page (a session ending) must leave its address to that navigation instead (boot-auth-1).
 */
export async function leaveOverlayHistoryEntry( router: Router, fallback: RouteLocationRaw ): Promise< void > {
    if ( isOverlayHistoryEntry( router ) ) {
        router.back();
        return;
    }

    try {
        await router.replace( fallback );
    } catch {
        // A guard that throws is reported by router/navigation-errors.ts; the overlay is closed, only the address lags
    }
}
